'use client';
import {
    faChevronDown,
    faChevronUp,
    faCircleCheck,
    faCircleXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { Content } from '@prismicio/client';
import { SliceComponentProps } from '@prismicio/react';
import classNames from 'classnames';
import maps from 'src/data/maps.json';
import MoshLogo from 'src/components/MoshLogo/MoshLogo';

export type ComparisonTablev2Props =
    SliceComponentProps<Content.ComparisonTablev2Slice>;

const SingleColumnComparison = ({
    slice,
}: {
    slice: Content.ComparisonTablev2SliceDefault;
}) => {
    const { colors } = maps;
    const [showAll, setShowAll] = useState(false);
    const maxVisibleItems = 4;
    const isDarkMode = !!slice.primary.dark_mode;

    const visibleItems = showAll
        ? slice.primary.column_elements
        : slice.primary.column_elements.slice(0, maxVisibleItems);

    return (
        <section data-testid="comparison-table-single-column">
            <div
                data-testid="comparison-table-mosh-column"
                className={classNames(
                    'container flex min-h-[450px] w-[300px] shrink-0 flex-col gap-8 text-wrap rounded-2xl border-2 border-green p-6',
                    {
                        'bg-green-dark': isDarkMode,
                    },
                )}
            >
                {slice.primary.heading_as_logo ? (
                    <div data-testid="comparison-table-mosh-logo">
                        <MoshLogo variant={isDarkMode ? 'white' : 'dark'} />
                    </div>
                ) : (
                    <h3
                        data-testid="comparison-table-mosh-heading"
                        className={classNames('font-demi', {
                            'text-white': isDarkMode,
                        })}
                    >
                        {slice.primary.heading}
                    </h3>
                )}

                <div
                    data-testid="comparison-table-features-list"
                    className="flex flex-col items-start gap-6 self-stretch"
                >
                    {visibleItems.map((item, index) => (
                        <div
                            data-testid={`comparison-table-feature-${index}`}
                            className="flex items-start gap-2 self-stretch"
                            key={index}
                        >
                            <FontAwesomeIcon
                                data-testid={`comparison-table-feature-icon-${index}`}
                                icon={faCircleCheck}
                                color={colors['green']}
                                size={'xl'}
                                className="pt-1"
                            />
                            <div className="flex flex-1 flex-col flex-wrap items-start justify-center gap-1">
                                <p
                                    data-testid={`comparison-table-feature-heading-${index}`}
                                    className={classNames(
                                        { 'text-white': isDarkMode },
                                        'text-[18px]',
                                    )}
                                >
                                    {item.subheading}
                                </p>
                                <p
                                    data-testid={`comparison-table-feature-text-${index}`}
                                    className={classNames({
                                        'text-white': isDarkMode,
                                    })}
                                >
                                    {item.subtext}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>

                {slice.primary.column_elements.length > maxVisibleItems && (
                    <button
                        data-testid="comparison-table-view-more-button"
                        onClick={() => setShowAll(!showAll)}
                        className={classNames('font-bold', {
                            'text-white': isDarkMode,
                        })}
                    >
                        {showAll ? 'View less ' : 'View more '}
                        <FontAwesomeIcon
                            icon={showAll ? faChevronUp : faChevronDown}
                            color={isDarkMode ? '#fcfcfc' : '#003d3b'}
                            className="text-sm"
                        />
                    </button>
                )}
            </div>
        </section>
    );
};

type ColumnElementItem = {
    which_column: string | number;
    subheading?: string;
    subtext: string;
    checked: boolean;
};

const ComparisonItem = ({
    item,
    isDarkMode,
    subtextOnly = false,
    testIdPrefix,
}: {
    item: ColumnElementItem;
    isDarkMode: boolean;
    subtextOnly?: boolean;
    testIdPrefix: string;
}) => {
    const { colors } = maps;
    const checked = !!item.checked;
    if (subtextOnly) {
        return (
            <div
                data-testid={`${testIdPrefix}-item`}
                className={classNames(
                    'flex flex-col items-start gap-2 self-stretch text-center',
                    subtextOnly ? 'flex-col items-center' : '',
                )}
            >
                <FontAwesomeIcon
                    data-testid={`${testIdPrefix}-icon`}
                    data-checked={checked}
                    icon={checked ? faCircleCheck : faCircleXmark}
                    color={checked ? colors['green'] : 'grey'}
                    size={'xl'}
                    className="shrink-0 pt-1"
                />
                <div className="flex w-full max-w-full flex-1 flex-col flex-wrap items-start justify-center gap-1 overflow-hidden break-words">
                    {!subtextOnly && item.subheading && (
                        <p
                            data-testid={`${testIdPrefix}-heading`}
                            className={classNames(
                                { 'text-white': isDarkMode },
                                'w-full break-words text-[18px]',
                            )}
                        >
                            {item.subheading}
                        </p>
                    )}
                    <p
                        data-testid={`${testIdPrefix}-text`}
                        className={classNames(
                            { 'text-white': isDarkMode },
                            'w-full break-words',
                        )}
                    >
                        {item.subtext}
                    </p>
                </div>
            </div>
        );
    } else {
        return (
            <div
                data-testid={`${testIdPrefix}-item`}
                className={classNames(
                    'flex items-start gap-2 self-stretch text-center',
                )}
            >
                <FontAwesomeIcon
                    data-testid={`${testIdPrefix}-icon`}
                    data-checked={checked}
                    icon={checked ? faCircleCheck : faCircleXmark}
                    color={checked ? colors['green'] : 'grey'}
                    size={'xl'}
                    className="shrink-0 pt-1"
                />
                <div className="flex w-full max-w-full flex-1 flex-col flex-wrap items-start gap-1 overflow-hidden break-words text-left">
                    <p
                        data-testid={`${testIdPrefix}-heading`}
                        className={classNames(
                            { 'text-white': isDarkMode },
                            'w-full break-words text-[18px]',
                        )}
                    >
                        {item.subheading}
                    </p>
                    <p
                        data-testid={`${testIdPrefix}-text`}
                        className={classNames(
                            { 'text-white': isDarkMode },
                            'w-full break-words',
                        )}
                    >
                        {item.subtext}
                    </p>
                </div>
            </div>
        );
    }
};

const MultiColumnComparison = ({
    slice,
}: {
    slice: Content.ComparisonTablev2SliceValueProp;
}) => {
    const isDarkMode = !!slice.primary.dark_mode;
    const subtextOnly = !!slice.primary.subtext_only;

    const numberOfCompetitors = Math.min(
        Math.max(0, slice.primary.number_of_competitors || 0),
        3,
    );

    const competitorNames = [
        slice.primary.competitor_1_name,
        slice.primary.competitor_2_name,
        slice.primary.competitor_3_name,
    ].slice(0, numberOfCompetitors);

    const columnItems: ColumnElementItem[][] = [[], [], [], []];

    (slice.primary.column_element as ColumnElementItem[]).forEach((item) => {
        const columnIndex =
            typeof item.which_column === 'number'
                ? item.which_column - 1
                : parseInt(item.which_column as string) - 1;

        if (columnIndex >= 0 && columnIndex < 4) {
            columnItems[columnIndex].push(item);
        }
    });

    const [moshItems, ...competitorItemColumns] = columnItems;

    return (
        <section
            data-testid="comparison-table-multi-column"
            data-dark-mode={isDarkMode}
            data-subtext-only={subtextOnly}
            className="flex w-full flex-col flex-nowrap items-center justify-center gap-8 px-4 py-8"
        >
            <div
                data-testid="comparison-table-header"
                className="container mx-auto flex w-full flex-col flex-nowrap items-center justify-center gap-2 self-stretch lg:max-w-[736px]"
            >
                <h2 data-testid="comparison-table-title">
                    {slice.primary.heading}
                </h2>
                <p
                    data-testid="comparison-table-subtitle"
                    className="text-[18px]"
                >
                    {slice.primary.subheading}
                </p>
            </div>

            <div
                data-testid="comparison-table-columns-container"
                data-competitors={numberOfCompetitors}
                className="hide-scrollbar flex items-center gap-4 self-stretch overflow-x-auto pr-4 lg:mx-auto"
            >
                <div
                    data-testid="comparison-table-mosh-column"
                    className={classNames(
                        'flex min-h-[450px] min-w-[200px] max-w-[400px] shrink-0 flex-col items-start gap-8 text-wrap rounded-2xl rounded-3xl border-2 border-green p-6',
                        isDarkMode ? 'bg-green-dark' : '',
                    )}
                >
                    <div data-testid="comparison-table-mosh-logo">
                        <MoshLogo variant={isDarkMode ? 'white' : 'dark'} />
                    </div>

                    <div
                        data-testid="comparison-table-mosh-features"
                        className="flex flex-col items-start gap-6 self-stretch"
                    >
                        {moshItems.map((item, index) => (
                            <ComparisonItem
                                key={index}
                                item={item}
                                isDarkMode={isDarkMode}
                                subtextOnly={subtextOnly}
                                testIdPrefix={`comparison-table-mosh-feature-${index}`}
                            />
                        ))}
                    </div>
                </div>

                {competitorItemColumns
                    .slice(0, numberOfCompetitors)
                    .map((items, index) => (
                        <div
                            key={index}
                            data-testid={`comparison-table-competitor-column-${index}`}
                            className={classNames(
                                'flex min-h-[450px] min-w-[200px] max-w-[400px] shrink-0 flex-col items-start gap-8 overflow-hidden rounded-3xl border-2 border-grey-200 p-6',
                            )}
                        >
                            <h3
                                data-testid={`comparison-table-competitor-heading-${index}`}
                                className={classNames('font-demi')}
                            >
                                {competitorNames[index] ||
                                    `Competitor ${index + 1}`}
                            </h3>

                            <div
                                data-testid={`comparison-table-competitor-features-${index}`}
                                className="flex flex-col items-start gap-6 self-stretch"
                            >
                                {items.map((item, itemIndex) => (
                                    <ComparisonItem
                                        key={itemIndex}
                                        item={item}
                                        isDarkMode={false}
                                        subtextOnly={subtextOnly}
                                        testIdPrefix={`comparison-table-competitor-${index}-feature-${itemIndex}`}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}
            </div>
        </section>
    );
};

const ComparisonTablev2 = ({ slice }: ComparisonTablev2Props): JSX.Element => {
    return slice.variation === 'valueProp' ? (
        <MultiColumnComparison slice={slice} />
    ) : (
        <SingleColumnComparison slice={slice} />
    );
};

export default ComparisonTablev2;
